import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { User } from 'lucide-react';

export default function Step1ClientInfo({ data, onNext }) {
  const [formData, setFormData] = useState({
    civilite: 'M.',
    nom: '',
    prenom: '',
    portable1: '',
    portable2: '',
    email: '',
    adresse: '',
    code_postal: '',
    ville: '',
    societe_installatrice: '',
  });

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nom || !formData.prenom || !formData.portable1 || !formData.email ||
        !formData.adresse || !formData.code_postal || !formData.ville || !formData.societe_installatrice) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    toast.success('Étape 1 validée');
    onNext(formData);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-2">
          <User className="w-8 h-8 text-accent" strokeWidth={1.5} />
        </div>
        <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Vos informations
        </h2>
        <p className="text-base text-foreground/80">
          Merci de renseigner vos coordonnées complètes
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          {/* Civilite */}
          <div>
            <label className="block text-sm font-medium mb-2">Civilité *</label>
            <select
              data-testid="civilite-select"
              name="civilite"
              value={formData.civilite}
              onChange={handleChange}
              className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
            >
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          {/* Nom & Prenom */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nom *</label>
              <input
                data-testid="nom-input"
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Prénom *</label>
              <input
                data-testid="prenom-input"
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                required
              />
            </div>
          </div>

          {/* Portables */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Portable 1 *</label>
              <input
                data-testid="portable1-input"
                type="tel"
                name="portable1"
                value={formData.portable1}
                onChange={handleChange}
                placeholder="06 12 34 56 78"
                className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Portable 2</label>
              <input
                data-testid="portable2-input"
                type="tel"
                name="portable2"
                value={formData.portable2}
                onChange={handleChange}
                placeholder="Optionnel"
                className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2">Email *</label>
            <input
              data-testid="email-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="votre.email@exemple.fr"
              className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
              required
            />
          </div>

          {/* Adresse */}
          <div>
            <label className="block text-sm font-medium mb-2">Adresse *</label>
            <input
              data-testid="adresse-input"
              type="text"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
              required
            />
          </div>

          {/* Code Postal & Ville */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Code Postal *</label>
              <input
                data-testid="code-postal-input"
                type="text"
                name="code_postal"
                value={formData.code_postal}
                onChange={handleChange}
                placeholder="75001"
                className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Ville *</label>
              <input
                data-testid="ville-input"
                type="text"
                name="ville"
                value={formData.ville}
                onChange={handleChange}
                className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                required
              />
            </div>
          </div>

          {/* Societe Installatrice */}
          <div>
            <label className="block text-sm font-medium mb-2">Société Installatrice *</label>
            <input
              data-testid="societe-input"
              type="text"
              name="societe_installatrice"
              value={formData.societe_installatrice}
              onChange={handleChange}
              className="w-full h-12 bg-white border border-input rounded-md px-4 text-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
              required
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          data-testid="next-step-btn"
          type="submit"
          className="w-full h-14 bg-accent text-accent-foreground text-lg font-medium rounded-md hover:bg-accent/90 border-b-2 border-accent-foreground/20 active:border-b-0 active:translate-y-[2px] transition-all"
        >
          Continuer
        </button>
      </form>
    </div>
  );
}